import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading, Toggle } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface Stat {
  id: number;
  icon: string;
  value: string;
  label_en: string;
  label_ro: string;
  is_dynamic: boolean;
  sort_order: number;
}

const StatsPage: React.FC = () => {
  const stats = useCrud<Stat>('/api/stats');
  const { showToast } = useToast();
  const [editingStat, setEditingStat] = useState<Stat | null>(null);

  useEffect(() => { stats.fetchAll(); }, []);

  const handleSave = async (stat: Partial<Stat>) => {
    if (editingStat?.id) {
      const result = await stats.update(editingStat.id, stat);
      if (result) {
        showToast('Stat updated!', 'success');
        setEditingStat(null);
      }
    } else {
      const result = await stats.create(stat);
      if (result) {
        showToast('Stat added!', 'success');
        setEditingStat(null);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this stat?')) {
      const result = await stats.remove(id);
      if (result) showToast('Stat deleted', 'success');
    }
  };

  if (stats.loading && stats.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Stats"
        subtitle="Manage the statistics shown in the About section"
        actions={
          <Button onClick={() => setEditingStat({ id: 0, icon: '📊', value: '', label_en: '', label_ro: '', is_dynamic: false, sort_order: stats.items.length })}>
            + Add Stat
          </Button>
        }
      />

      <div className="admin-content">
        <Card>
          <div className="admin-list-editor">
            {stats.items.map(stat => (
              <div key={stat.id} className="admin-list-item">
                <span style={{ fontSize: '1.5rem' }}>{stat.icon}</span>
                <div className="admin-list-item-content">
                  <div>
                    <strong>{stat.value}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {stat.label_en} {stat.is_dynamic && '(dynamic)'}
                    </div>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  <Button size="sm" variant="secondary" onClick={() => setEditingStat(stat)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(stat.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {stats.items.length === 0 && (
              <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                No stats yet. Add your first stat above.
              </p>
            )}
          </div>
        </Card>

        {editingStat && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingStat(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingStat.id ? 'Edit Stat' : 'Add Stat'}</h3>
              <StatForm
                stat={editingStat}
                onSave={handleSave}
                onCancel={() => setEditingStat(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const StatForm: React.FC<{
  stat: Stat;
  onSave: (s: Partial<Stat>) => void;
  onCancel: () => void;
}> = ({ stat, onSave, onCancel }) => {
  const [form, setForm] = useState(stat);
  const [lang, setLang] = useState<'en' | 'ro'>('en');

  return (
    <div className="admin-form">
      <Input label="Icon (emoji)" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="📊" />
      <Input label="Value" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="10+" required />

      <LanguageTabs currentLang={lang} onChange={setLang} />
      {lang === 'en' ? (
        <Input label="Label (EN)" value={form.label_en} onChange={e => setForm(f => ({ ...f, label_en: e.target.value }))} placeholder="Releases" required />
      ) : (
        <Input label="Label (RO)" value={form.label_ro} onChange={e => setForm(f => ({ ...f, label_ro: e.target.value }))} placeholder="Lansări" required />
      )}

      <Toggle
        checked={form.is_dynamic}
        onChange={checked => setForm(f => ({ ...f, is_dynamic: checked }))}
        label="Dynamic value (fetched from external source)"
      />
      <p style={{ fontSize: '0.8rem', color: 'var(--admin-text-dim)', marginTop: -8 }}>
        Dynamic stats like "Monthly Listeners" can be updated automatically.
      </p>

      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default StatsPage;
